/**
 * Children API — narrow client over POST/GET /v1/child.
 *
 * Phase 1C.1 endpoints (deployed; verified by 8 vitest unit tests):
 *  - POST /v1/child          create child_profile
 *  - GET  /v1/child          list children for device
 *  - GET  /v1/child/:id      get one (404 if cross-device)
 *  - PUT  /v1/child/:id      partial update (.strict, ≥1 field)
 *  - DELETE /v1/child/:id    hard delete + cascade
 *
 * Only POST and GET are wired in this file — the rest will be added in
 * Phase 5 (Parent Zone) when edit/delete flows land.
 *
 * Validation contract (zod in apps/api/src/lib/validators.ts):
 *  - name     1-20 chars
 *  - age      int 2-6
 *  - classId  literal 1|2|3
 *  - avatarId int 0-99, default 0
 *  - isDefault bool, default true
 *
 * Server is the source of truth — a stale age→classId map here fails with
 * a 400 (zod refuses) rather than silent corruption.
 */

import { apiFetch } from './client';
import { ageToClassId } from '../age-class';
import type { AgeBracket, ClassId, ChildProfile } from '@funpaper/shared';

export type CreateChildInput = {
  name: string;
  age: AgeBracket;
  avatarId?: number;
  isDefault?: boolean;
  /** Test-only: override the derived classId. */
  classIdOverride?: ClassId;
};

type CreateChildResponse = {
  ok: true;
  child: ChildProfile;
};

export async function createChild(input: CreateChildInput): Promise<ChildProfile> {
  const classId = input.classIdOverride ?? ageToClassId(input.age);
  const body = {
    name: input.name,
    age: input.age,
    classId,
    avatarId: input.avatarId ?? 0,
    isDefault: input.isDefault ?? true,
  };
  const response = await apiFetch<CreateChildResponse>('/v1/child', {
    method: 'POST',
    body,
  });
  return response.child;
}

type ListChildrenResponse = {
  ok: true;
  count: number;
  children: ChildProfile[];
};

export async function listChildren(): Promise<ChildProfile[]> {
  const response = await apiFetch<ListChildrenResponse>('/v1/child');
  return response.children;
}

/**
 * Get the device's default child (Y1 = single child per device).
 * Returns null if no child has been created yet.
 */
export async function getActiveChild(): Promise<ChildProfile | null> {
  const children = await listChildren();
  if (children.length === 0) return null;
  return children.find((c) => c.isDefault) ?? children[0] ?? null;
}

export type UpdateChildInput = {
  name?: string;
  age?: number;
  classId?: 1 | 2 | 3;
  avatarId?: number;
  isDefault?: boolean;
};

/** PUT /v1/child/:id — partial update (Phase 5.3, Parent Zone). */
export async function updateChild(
  childId: number,
  input: UpdateChildInput,
): Promise<ChildProfile> {
  const response = await apiFetch<{ ok: true; child: ChildProfile }>(
    `/v1/child/${childId}`,
    { method: 'PUT', body: input },
  );
  return response.child;
}

/** DELETE /v1/child/:id — hard delete + cascade (Phase 5.4, Hapus Data Saya). */
export async function deleteChild(childId: number): Promise<void> {
  await apiFetch<{ ok: true }>(`/v1/child/${childId}`, { method: 'DELETE' });
}
